// ============================================
// HOOK - RUT Formatter (Chilean Standard)
// ============================================

import { useState, useCallback } from 'react';

/**
 * Limpia el RUT dejando solo números y K
 */
export const cleanRut = (value: string): string => {
  return value
    .toUpperCase()
    .replace(/[^0-9K]/g, '');
};

/**
 * Formatea el RUT con puntos y guión según estándar chileno
 * Ejemplo: 177281336 -> 17.728.133-6
 */
export const formatRut = (value: string): string => {
  const cleaned = cleanRut(value);
  
  if (cleaned.length === 0) return '';
  
  // Separar el dígito verificador (última letra o número)
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  
  if (body.length === 0) return dv;
  
  // Formatear el cuerpo con puntos cada 3 dígitos desde el final
  const formattedBody = body
    .split('')
    .reverse()
    .reduce((acc, digit, index) => {
      if (index > 0 && index % 3 === 0) {
        return digit + '.' + acc;
      }
      return digit + acc;
    }, '');
  
  return `${formattedBody}-${dv}`;
};

/**
 * Calcula el dígito verificador de un RUT
 * @param rut - RUT sin dígito verificador (solo números)
 * @returns Dígito verificador (0-9 o K)
 */
export const calculateDv = (rut: string | number): string => {
  const body = rut.toString().replace(/[^0-9]/g, '');
  
  if (body.length === 0) return '';
  
  let sum = 0;
  let multiplier = 2;
  
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const expectedDv = 11 - (sum % 11);
  return expectedDv === 11 ? '0' : expectedDv === 10 ? 'K' : expectedDv.toString();
};

/**
 * Formatea un RUT calculando su dígito verificador
 * @param rut - RUT sin dígito verificador
 * @returns RUT formateado con puntos, guión y DV (ej: 17.728.133-6)
 */
export const formatRutWithDv = (rut: string | number): string => {
  const body = rut.toString().replace(/[^0-9]/g, '');
  
  if (body.length === 0) return '';
  
  const dv = calculateDv(body);
  const rutWithDv = body + dv;
  
  return formatRut(rutWithDv);
};

/**
 * Valida que el dígito verificador sea correcto
 */
export const validateRut = (rut: string): boolean => {
  const cleaned = cleanRut(rut);
  
  if (cleaned.length < 2) return false;
  
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  
  const expectedDv = calculateDv(body);
  
  return dv === expectedDv;
};

/**
 * Hook para manejar el formateo automático del RUT
 */
export const useRutFormatter = (initialValue: string = '') => {
  const [rawValue, setRawValue] = useState(cleanRut(initialValue));
  const [formattedValue, setFormattedValue] = useState(formatRut(initialValue));

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cleaned = cleanRut(inputValue);
    
    // Limitar a 9 caracteres (8 números + 1 dígito verificador)
    const limited = cleaned.slice(0, 9);
    
    setRawValue(limited);
    setFormattedValue(formatRut(limited));
  }, []);

  const reset = useCallback(() => {
    setRawValue('');
    setFormattedValue('');
  }, []);

  const isValid = useCallback(() => {
    return validateRut(rawValue);
  }, [rawValue]);

  return {
    rawValue,        // Valor sin formato (para enviar al backend)
    formattedValue,  // Valor formateado (para mostrar)
    handleChange,    // Handler para el onChange del input
    reset,           // Para limpiar el campo
    isValid,         // Para validar el RUT
  };
};
